// ============================================
// Pagination Component
// Supports two variants: numbered (Products) and simple (Inventory)
// ============================================

import { cn } from '../../utils/cn';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { useI18n } from '../../i18n';

// ============================================
// Types
// ============================================

type PaginationVariant = 'numbered' | 'simple';

interface PaginationProps {
  /** Pagination style variant */
  variant?: PaginationVariant;
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items */
  totalItems: number;
  /** Items per page */
  pageSize: number;
  /** Page change handler */
  onPageChange: (page: number) => void;
  /** Label for items (e.g., "products", "results") */
  itemLabel?: string;
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function Pagination({
  variant = 'numbered',
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemLabel = 'items',
  className,
}: PaginationProps) {
  const { t } = useI18n();
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  // Compact page list: first + last always shown, a 3-wide block around the
  // current page, and an ellipsis wherever a gap is collapsed.
  //   near start:  1 2 3 … last
  //   middle:      1 … c-1 c c+1 … last
  //   near end:    1 … last-2 last-1 last
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const total = totalPages;
    if (total <= 1) return [1];

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(total - 1, currentPage + 1);

    // Pin a 3-wide block to the edge instead of leaving a lone number.
    if (currentPage <= 3) {
      start = 2;
      end = Math.min(total - 1, 3);
    } else if (currentPage >= total - 2) {
      start = Math.max(2, total - 2);
      end = total - 1;
    }

    const pages: (number | 'ellipsis')[] = [1];
    if (start > 2) pages.push('ellipsis');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('ellipsis');
    pages.push(total);
    return pages;
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between gap-4 px-6 py-4 sm:flex-row',
        className
      )}
    >
      {/* Left side - Showing X of Y */}
      <p className="text-xs font-medium text-slate-500">
        {t('showing')} <span className="font-semibold text-slate-900">{endItem}</span>{' '}
        {t('of')} <span className="font-semibold text-slate-900">{totalItems.toLocaleString()}</span>{' '}
        {t(itemLabel as any) || itemLabel}
      </p>

      {/* Right side - Navigation */}
      <div className="flex min-w-0 max-w-full items-center gap-2">
        {variant === 'simple' ? (
          // Simple variant (Previous / Next buttons)
          <>
            <button
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className={cn(
                'rounded-lg border px-4 py-2 text-xs font-bold transition-colors',
                canGoPrevious
                  ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  : 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'
              )}
            >
              {t('previous')}
            </button>
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className={cn(
                'rounded-lg px-4 py-2 text-xs font-bold transition-colors',
                canGoNext
                  ? 'bg-[#004AC6] text-white hover:bg-[#003da3]'
                  : 'cursor-not-allowed bg-slate-200 text-slate-400'
              )}
            >
              {t('next')}
            </button>
          </>
        ) : (
          // Numbered variant (arrows + page numbers)
          <>
            {/* Previous arrow */}
            <button
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[#E5E7EB] bg-white transition-colors',
                canGoPrevious
                  ? 'text-[#374151] hover:bg-slate-50'
                  : 'cursor-not-allowed text-[#D1D5DB]'
              )}
              aria-label="Previous page"
            >
              <ChevronLeftIcon size={16} />
            </button>

            {/* Page numbers */}
            <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
              {getPageNumbers().map((page, idx) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="flex h-8 w-8 shrink-0 items-center justify-center text-xs text-slate-400">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded text-xs font-semibold transition-colors',
                      page === currentPage
                        ? 'border border-[#3B82F6] bg-white text-[#3B82F6]'
                        : 'border border-transparent bg-white text-[#374151] hover:border-[#E5E7EB] hover:bg-slate-50'
                    )}
                  >
                    {page}
                  </button>
                )
              )}
            </div>

            {/* Next arrow */}
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[#E5E7EB] bg-white transition-colors',
                canGoNext
                  ? 'text-[#374151] hover:bg-slate-50'
                  : 'cursor-not-allowed text-[#D1D5DB]'
              )}
              aria-label="Next page"
            >
              <ChevronRightIcon size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
